import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, AlertTriangle, XCircle, Clock, TrendingUp, RotateCcw, ShieldCheck, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPublicSettings } from "@/lib/api";

const CONFIG = {
  qualificato: { icon: CheckCircle2, color: "#059669", bg: "bg-emerald-50", ring: "ring-emerald-200", badge: "QUALIFICATO", badgeClass: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  consolidamento: { icon: TrendingUp, color: "#1E3A8A", bg: "bg-blue-50", ring: "ring-blue-200", badge: "CONSOLIDAMENTO", badgeClass: "bg-blue-100 text-blue-800 border-blue-300" },
  "ad-agio": { icon: Clock, color: "#D97706", bg: "bg-amber-50", ring: "ring-amber-200", badge: "IN INCUBATORE", badgeClass: "bg-amber-100 text-amber-800 border-amber-300" },
  ko: { icon: XCircle, color: "#DC2626", bg: "bg-rose-50", ring: "ring-rose-200", badge: "NON FATTIBILE", badgeClass: "bg-rose-100 text-rose-800 border-rose-300" },
};

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const CARD_MOTION = { initial: { opacity: 0, scale: 0.96 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 0.4, ease: "easeOut" } };
const ICON_MOTION = { initial: { scale: 0 }, animate: { scale: 1 }, transition: { delay: 0.15, type: "spring" } };

export default function ResultScreen({ result, onRestart }) {
  const cfg = CONFIG[result.esito_algoritmo] || CONFIG.ko;
  const Icon = cfg.icon;
  const [wa, setWa] = useState(null);
  useEffect(() => { getPublicSettings().then((s) => setWa(s.whatsapp)).catch(() => {}); }, []);
  const waNumber = (wa?.telefono || "").replace(/\D/g, "");
  const waHref = waNumber && `https://wa.me/${waNumber.startsWith("39") || waNumber.length > 10 ? waNumber : "39" + waNumber}?text=${encodeURIComponent(
    `Ciao${wa?.nome_consulente ? " " + wa.nome_consulente : ""}, ho appena completato la verifica (${result.esito_titolo}). Sono ${result.contatto?.nome || ""}, quando possiamo sentirci?`)}`;

  return (
    <motion.div {...CARD_MOTION} className="w-full max-w-2xl mx-auto">
      <div className={`rounded-2xl border border-border bg-card shadow-xl overflow-hidden`} data-testid="simulation-result">
        <div className={`${cfg.bg} p-8 text-center ring-1 ${cfg.ring}`}>
          <motion.div {...ICON_MOTION}
            className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-white shadow-md mb-4">
            <Icon className="h-10 w-10" style={{ color: cfg.color }} strokeWidth={2} />
          </motion.div>
          <div className="flex justify-center mb-3">
            <span data-testid="simulation-outcome-badge"
              className={`inline-block text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border ${cfg.badgeClass}`}>
              {cfg.badge}
            </span>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-primary tracking-tight">{result.esito_titolo}</h2>
          <p className="text-muted-foreground mt-3 max-w-md mx-auto">{result.esito_messaggio}</p>
        </div>

        <div className="p-6 sm:p-8 space-y-6">
          {/* Scores */}
          <div className="grid grid-cols-2 gap-4">
            <ScoreCard testid="lead-score-categorical" label="Score categoriale"
              value={`${result.score_categoriale}/10`} sub={result.score_categoriale_label} />
            <ScoreCard testid="lead-score-insurance" label="Score assicurativo"
              value={`${result.score_assicurativo}/100`} sub={result.esito_assicurativo.toUpperCase()} />
          </div>

          {/* Rata */}
          {result.esito_algoritmo !== "ko" && (
            <div className="rounded-xl border border-border p-5 bg-secondary/40" data-testid="result-simulazione">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">La tua simulazione indicativa</p>
              {result.simulazione?.consigliato ? (
                <div className="text-center mb-4" data-testid="result-rata-stimata">
                  <div className="text-sm text-muted-foreground">Per {fmt(result.simulazione.importo)} potresti pagare circa</div>
                  <div className="font-display font-extrabold text-4xl text-accent mt-1">{fmt(result.simulazione.consigliato.rata)}<span className="text-base font-semibold text-muted-foreground">/mese</span></div>
                  <div className="text-xs text-muted-foreground mt-1">per {result.simulazione.consigliato.durata_mesi} mesi · trattenuta direttamente in busta paga o pensione</div>
                </div>
              ) : result.simulazione?.importo_massimo_120 > 0 && (
                <div className="text-center mb-4" data-testid="result-importo-max">
                  <div className="text-sm text-muted-foreground">Con la tua rata massima puoi ottenere fino a circa</div>
                  <div className="font-display font-extrabold text-4xl text-accent mt-1">{fmt(result.simulazione.importo_massimo_120)}</div>
                  <div className="text-xs text-muted-foreground mt-1">in 120 mesi · il consulente verificherà l'importo esatto</div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3 text-center border-t border-border pt-3">
                <div>
                  <div className="font-mono font-bold text-lg text-primary">{fmt(result.rata.netto_stimato)}</div>
                  <div className="text-xs text-muted-foreground mt-1">Netto stimato</div>
                </div>
                <div>
                  <div className="font-mono font-bold text-lg text-primary">{fmt(result.rata.rata_massima)}</div>
                  <div className="text-xs text-muted-foreground mt-1">Rata max (20%)</div>
                </div>
                <div>
                  <div className="font-mono font-bold text-lg text-emerald-600">{fmt(result.rata.residuo_netto)}</div>
                  <div className="text-xs text-muted-foreground mt-1">Residuo netto</div>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-3 leading-snug">Simulazione puramente indicativa (TAN {(result.simulazione?.tan_indicativo * 100 || 0).toFixed(1)}%), non costituisce offerta. Condizioni definitive comunicate dal consulente e dall'istituto erogante.</p>
            </div>
          )}

          {/* KO reasons */}
          {result.ko_reasons?.length > 0 && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-5">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-rose-600" />
                <p className="text-sm font-semibold text-rose-800">Elementi rilevati dall'algoritmo</p>
              </div>
              <ul className="space-y-1">
                {result.ko_reasons.map((r) => (
                  <li key={r} className="text-sm text-rose-700 flex gap-2"><span>•</span>{r}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            Dati trattati con consenso esplicito • Trasmissione a consulente iscritto OAM
          </div>

          {result.esito_algoritmo !== "ko" && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="result-callback-info">
              <strong>{wa?.nome_consulente ? `${wa.nome_consulente}, il tuo consulente,` : "Un consulente dedicato"}</strong> ti richiamerà {wa?.fascia_richiamo || "a breve"}.
              {waHref && (
                <a href={waHref} target="_blank" rel="noreferrer" data-testid="result-whatsapp-button"
                  className="mt-3 flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold py-3 transition-colors">
                  <MessageCircle className="h-5 w-5" /> Scrivici su WhatsApp
                </a>
              )}
            </div>
          )}

          <Button data-testid="result-restart-button" variant="outline" onClick={onRestart} className="w-full gap-2">
            <RotateCcw className="h-4 w-4" /> Nuova simulazione
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

const ScoreCard = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-border p-4 bg-card">
    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
    <div className="font-mono font-bold text-3xl text-primary mt-1">{value}</div>
    <p className="text-xs text-muted-foreground mt-1 truncate">{sub}</p>
  </div>
);
